import React, { useMemo, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Form } from 'react-form';
import PropTypes, { any } from 'prop-types';

import CourtDeskSidePanel from './CourtDeskSidePanel';
import { updateEventDrawScoringTypeOverrides } from '../../../utils/events';
import {
    ballTypeIds,
    divisionFormatTypeIds,
    drawCategoryTypeIds,
    drawEliminationTypeIds,
    drawScheduleTypeIds,
    getPlayersCountListToForwardForPlayoff,
    officialsTypeIds,
} from '../../../utils/eventConstants';
import { CheckBox, Select } from '../../../components/Form';
import { required } from '../../../utils/validation';
import { allowedMatchTimes } from '../../../utils/allottedTimeConstants';
import ScoringTypeAllottedTimeField from '../../../components/PopUps/EventDrawCreateEditPopup/ScoringTypeAllottedTimeField';
import { updateDrawSettings } from '../../../actions/court-scheduler-actions';
import useDrawEdit from '../../../components/PopUps/EventDrawCreateEditPopup/useDrawEdit';

import styles from './courtSchedular.scss';
import { getDrawData } from '../../../utils/courtSchedular';
import { shouldDisplayPlayOffDrawSection } from '../../../components/PopUps/EventDrawCreateEditPopup/utils';
import QualifyingDrawSection from '../../../components/PopUps/EventDrawCreateEditPopup/QualifyingDrawSection';
import GroupDrawFormatFields from '../../../components/PopUps/EventDrawCreateEditPopup/GroupDrawFormatFields';
import { getPlayersOrTeamsCountPlacedInDraw } from '../../../utils/draws';

const DrawEditConfig = (props) => {
    const {
        eventSchedule,
        eventId,
        draw,
        className,
        closeSideBar,
        sportTypeId,
        eventTypeId,
        isVerifiedEvent,
        eventAllottedMatchTimes,
        scheduleTypeId,
        drawDivision,
        allowMomentTimeZoneOverride,
    } = props;

    const dispatch = useDispatch();
    const courtScheduler = useSelector(({ courtScheduler }) => courtScheduler);
    const nextAvailable = useSelector(({ nextAvailable }) => nextAvailable);
    const drawEdit = useSelector(({ drawEdit }) => drawEdit);
    const { players, teams } = drawEdit;

    const drawData = useMemo(() => {
        if (scheduleTypeId === drawScheduleTypeIds.NEXT_AVAILABLE) {
            return getDrawData(nextAvailable.draws, draw.id);
        }

        return getDrawData(courtScheduler.drawsList, draw.id);
    }, [scheduleTypeId, nextAvailable.draws, courtScheduler.drawsList, draw.id]);
    const { parentDraw } = drawData;
    const [isVisible, setIsVisible] = useState(false);
    const forceDoubles = false;

    const {
        isAllottedTimeFlagEnabled,
        selectableDrawEliminationTypes,
        proDrawEliminationTypes,
        drawDataFromEditSpecificApi,
        playOffDrawOptions,
        scoringFormatOverrideOptions,
        onChangeScoringType,
        getScoringFormats,
        getDefaultValuesFromDrawSpecificData,
    } = useDrawEdit({
        sportTypeId,
        eventAllottedMatchTimes,
        isInEditMode: true,
        eventId,
        draw,
        timeZone: eventSchedule?.timeZone,
        isVerifiedEvent,
        forceDoubles,
        allowMomentTimeZoneOverride,
    });

    const validateAllottedTimeField = (value) => {
        if (!drawDataFromEditSpecificApi?.hasScheduledMatches) {
            return required(value);
        }

        return null;
    };

    const defaultValues = useMemo(() => {
        if (drawDataFromEditSpecificApi) {
            const { hasScheduledMatches, ...others } = drawDataFromEditSpecificApi;
            return getDefaultValuesFromDrawSpecificData(
                others,
                parentDraw,
                drawDivision,
            );
        }

        return null;
    }, [drawDataFromEditSpecificApi, draw, drawDivision, eventSchedule?.timeZone]);

    useEffect(() => {
        setIsVisible(true);
    }, []);

    const handleClose = () => {
        setIsVisible(false);
    };

    const onFormSubmit = async (values) => {
        const {
            areOfficialsAllowed,
            ballTypeId,
            qualifyingDraw,
            preQualifyingDraw,
            ...otherValues
        } = values;

        if (otherValues.formatTypeId === divisionFormatTypeIds.DINGLES) {
            otherValues.isRated = false;
        }

        // eslint-disable-next-line no-unused-vars
        let divisionIds = Object.entries(values.divisionIds || {}).length ? Object.entries(values.divisionIds).filter(([key, val]) => val).map(([key, val]) => key.slice(1)) : [];

        const isGroupDraw = parentDraw?.categoryTypeId === drawCategoryTypeIds.GROUPED;
        if (isGroupDraw) {
            // eslint-disable-next-line no-unused-vars
            divisionIds = Object.entries(parentDraw.divisionIds || {}).length ? Object.entries(values.divisionIds).filter(([key, val]) => val).map(([key, val]) => key.slice(1)) : [];
        }

        const updatedValues = updateEventDrawScoringTypeOverrides({
            ...otherValues,
            officialsTypeId: areOfficialsAllowed ? officialsTypeIds.YES : officialsTypeIds.NO,
            ballTypeId: parseInt(ballTypeId) !== parseInt(ballTypeIds.YELLOW) ? ballTypeId : null,
            divisionIds,
            ...(qualifyingDraw?.enabled ? { qualifyingDraw } : {}),
            ...(preQualifyingDraw?.enabled ? { preQualifyingDraw } : {}),
        });

        dispatch(updateDrawSettings(eventId, draw.id, updatedValues, sportTypeId, scheduleTypeId));
        closeSideBar();
    };

    const selectedCount = useMemo(() => {
        return getPlayersOrTeamsCountPlacedInDraw(
            draw,
            players?.data,
            teams?.data,
        );
    }, [draw, players?.data, teams?.data]);

    if (!drawDataFromEditSpecificApi || !defaultValues) {
        return null;
    }

    return (
        <Form
            onSubmit={onFormSubmit}
            defaultValues={defaultValues}
        >
            {(formApi) => {
                const { submitForm, values } = formApi;
                const playersToAdvanceOptions = getPlayersCountListToForwardForPlayoff(2, parseInt(values.drawSize));

                return (
                    <form onSubmit={submitForm} id="dayConfigForm">
                        <CourtDeskSidePanel
                            className={className}
                            closeSideBar={handleClose}
                            isVisible={isVisible}
                            onClose={() => { closeSideBar(); }}
                        >
                            <div className={styles.header} data-testid="court-scheduler.draw-config.title">Edit {draw.name}</div>
                            <div className={styles.content}>
                                <div className={styles.section}>
                                    <h6 className={styles.sectionTitle} data-testid="court-scheduler.draw-config.draw-format-title">
                                        Draw Format
                                    </h6>
                                    <Select
                                        id="eliminationTypeId"
                                        label="Draw Type"
                                        field="eliminationTypeId"
                                        options={selectableDrawEliminationTypes}
                                        validate={required}
                                        disabled
                                        themeV3
                                        data-testid="court-scheduler.draw-config.draw-type-select"
                                    />
                                    {parseInt(values.eliminationTypeId) === drawEliminationTypeIds.PRO_DRAWS && (
                                        <Select
                                            id="eliminationTypeId"
                                            label="Parent Draw type"
                                            field="parentDrawEliminationTypeId"
                                            options={proDrawEliminationTypes}
                                            validate={required}
                                            disabled
                                            themeV3
                                            data-testid="court-scheduler.draw-config.parent-draw-type-select"
                                        />
                                    )}
                                    {shouldDisplayPlayOffDrawSection(true, parentDraw, draw) && playOffDrawOptions?.map((playOffDrawOption) => {
                                        return (
                                            <CheckBox
                                                key={`playoff-draw-option-${playOffDrawOption.id}`}
                                                id={playOffDrawOption.id}
                                                customLabel={`Add ${playOffDrawOption.name}`}
                                                field={playOffDrawOption.fieldName}
                                                disabled
                                                data-testid={`court-scheduler.draw-config.playoff-${playOffDrawOption.id}-checkbox`}
                                            />
                                        );
                                    })}
                                    {parseInt(values.categoryTypeId) !== drawCategoryTypeIds.GROUPED && shouldDisplayPlayOffDrawSection(true, parentDraw, draw) && parseInt(values.eliminationTypeId) === drawEliminationTypeIds.ROUND_ROBIN && values.createPlayoffDraw && (
                                        <>
                                            <Select
                                                id="groupPlayersAdvancingToPlayoffCount"
                                                field="groupPlayersAdvancingToPlayoffCount"
                                                label="# of players / teams to advance"
                                                hiddenDefault
                                                options={playersToAdvanceOptions}
                                                attributeToShow="label"
                                                attributeToSave="value"
                                                themeV3
                                                disabled
                                                data-testid="court-scheduler.draw-config.players-advancing-select"
                                            />
                                            {values.groupPlayersAdvancingToPlayoffCount >= 4 && (
                                                <CheckBox
                                                    id="createPlayoffBronzeMatch"
                                                    customLabel="Add a bronze medal playoff match"
                                                    field="createPlayoffBronzeMatch"
                                                    disabled
                                                    data-testid="court-scheduler.draw-config.bronze-match-checkbox"
                                                />
                                            )}
                                        </>
                                    )}
                                    {parseInt(values.categoryTypeId) === drawCategoryTypeIds.GROUPED && (
                                        <GroupDrawFormatFields
                                            values={values}
                                            isInEditMode={true}
                                            playOffDrawOptions={playOffDrawOptions}
                                            selectedCount={selectedCount}
                                            hideAutofillTypeDescriptions={true}
                                        />
                                    )}
                                </div>
                                <div className={styles.section}>
                                    <h6 className={styles.sectionTitle} data-testid="court-scheduler.draw-config.scoring-formats-title">
                                        Scoring Formats
                                    </h6>
                                    {!isAllottedTimeFlagEnabled && (
                                        <Select
                                            id="scoringTypeId"
                                            label="Default scoring format"
                                            field="scoringTypeId"
                                            options={getScoringFormats(values)}
                                            themeV3
                                            data-testid="court-scheduler.draw-config.scoring-type-select"
                                        />
                                    )}
                                    {isAllottedTimeFlagEnabled && (
                                        <div className="d-flex align-items-start">
                                            <div className="flex-1">
                                                <Select
                                                    id="scoringTypeId"
                                                    label="Default scoring format"
                                                    field="scoringTypeId"
                                                    options={getScoringFormats(values)}
                                                    themeV3
                                                    formGroupCssClass={`mb0`}
                                                    validate={required}
                                                    onChange={onChangeScoringType(formApi)}
                                                    data-testid="court-scheduler.draw-config.scoring-type-select"
                                                />
                                            </div>
                                            <div className="flex-1 ml16">
                                                <Select
                                                    id="allottedMatchTime"
                                                    field="allottedMatchTime"
                                                    label="Default allotted time"
                                                    options={allowedMatchTimes}
                                                    themeV3
                                                    formGroupCssClass={`mb0`}
                                                    validate={validateAllottedTimeField}
                                                    disabled={drawDataFromEditSpecificApi?.hasScheduledMatches}
                                                    data-testid="court-scheduler.draw-config.allotted-time-select"
                                                />
                                            </div>
                                        </div>
                                    )}
                                    {isAllottedTimeFlagEnabled && scoringFormatOverrideOptions.map((scoringFormatOverride) => {
                                        return (
                                            <ScoringTypeAllottedTimeField
                                                key={`override-${scoringFormatOverride.id}`}
                                                scoringFormatOverride={scoringFormatOverride}
                                                scoringTypeOptions={getScoringFormats(values)}
                                                formApi={formApi}
                                                values={values}
                                                eventAllottedMatchTimes={eventAllottedMatchTimes}
                                                disableAllottedTimesField={drawDataFromEditSpecificApi?.hasScheduledMatches}
                                            />
                                        );
                                    })}
                                </div>
                                {parseInt(values.eliminationTypeId) === drawEliminationTypeIds.PRO_DRAWS && (
                                    <QualifyingDrawSection
                                        sportTypeId={sportTypeId}
                                        eventTypeId={eventTypeId}
                                        className={styles.section}
                                        field="qualifyingDraw"
                                        formApi={formApi}
                                        qualifyingDrawType="qualifying"
                                        isInEditMode={true}
                                        forceDoubles={forceDoubles}
                                        isVerifiedEvent={isVerifiedEvent}
                                        eventAllottedMatchTimes={eventAllottedMatchTimes}
                                        drawDataFromEditSpecificApi={drawDataFromEditSpecificApi}
                                        isInEditConfig={true}
                                        eventSchedule={eventSchedule}
                                    />
                                )}
                                {parseInt(values.eliminationTypeId) === drawEliminationTypeIds.PRO_DRAWS && values.qualifyingDraw?.enabled && (
                                    <QualifyingDrawSection
                                        sportTypeId={sportTypeId}
                                        eventTypeId={eventTypeId}
                                        className={styles.section}
                                        field="preQualifyingDraw"
                                        formApi={formApi}
                                        qualifyingDrawType="pre-qualifying"
                                        isInEditMode={true}
                                        forceDoubles={forceDoubles}
                                        isVerifiedEvent={isVerifiedEvent}
                                        eventAllottedMatchTimes={eventAllottedMatchTimes}
                                        drawDataFromEditSpecificApi={drawDataFromEditSpecificApi}
                                        isInEditConfig={true}
                                        eventSchedule={eventSchedule}
                                    />
                                )}
                            </div>
                            <div className={`${styles.footer} d-flex`}>
                                <button
                                    className={'btn btn-default mr8 flex-1'}
                                    type="button"
                                    onClick={handleClose}
                                    data-testid="court-scheduler.draw-config.cancel-btn"
                                >
                                    Cancel
                                </button>
                                <button
                                    className={`btn btn-primary flex-1`}
                                    type="submit"
                                    data-testid="court-scheduler.draw-config.save-btn"
                                >
                                    Save Settings
                                </button>
                            </div>
                        </CourtDeskSidePanel>
                    </form>
                );
            }}
        </Form>
    );
};

DrawEditConfig.propTypes = {
    className: PropTypes.string,
    closeSideBar: PropTypes.func,
    eventAllottedMatchTimes: PropTypes.arrayOf(any),
    eventId: PropTypes.number.isRequired,
    draw: PropTypes.objectOf(any).isRequired,
    eventSchedule: PropTypes.objectOf(any).isRequired,
    sportTypeId: PropTypes.number.isRequired,
    eventTypeId: PropTypes.number.isRequired,
    isVerifiedEvent: PropTypes.bool,
    scheduleTypeId: PropTypes.number,
    drawDivision: PropTypes.objectOf(any),
    allowMomentTimeZoneOverride: PropTypes.bool,
};

DrawEditConfig.defaultProps = {
    className: '',
    closeSideBar: () => {},
    isVerifiedEvent: false,
    eventAllottedMatchTimes: [],
    scheduleTypeId: drawScheduleTypeIds.ORDER_OF_PLAY,
    drawDivision: null,
    allowMomentTimeZoneOverride: true,
};

export default DrawEditConfig;
